<i18n>
{
  "en": {
    "alreadyHaveAccount": "Already have an account?",
    "confirmPassword": "Confirm Password",
    "createAccount": "Create my account",
    "createAccountTitle": "Create an account",
    "email": "Email",
    "emailAlreadyUsed": "Email address already in use, please connect to your account",
    "incorrect_cvc": "The card’s security code is incorrect. Check the card’s security code or use a different card.",
    "title": "Gather your files with {appName}",
    "login": "Log in",
    "password": "Password",
    "pricing": "{appName} is only ${price} a month after your {trial} day free trial!",
    "requires_action": "You must complete additional action",
    "requires_payment_method": "Payment failed, please choose another card",
    "stripeSecurityInformation": "Your payment information will be securely processed by Stripe. If you're unhappy for any reason, we'll refund your unused subscription balance—no questions asked."
  },
  "fr": {
    "alreadyHaveAccount": "Vous avez déjà un compte ?",
    "confirmPassword": "Confirmer le mot de passe",
    "createAccount": "Créer mon compte",
    "createAccountTitle": "Créer un compte",
    "email": "Courriel",
    "emailAlreadyUsed": "Addresse email déjà utilisée, veuillez vous connecter à votre compte",
    "incorrect_cvc": "Incorrect CVC",
    "title": "Rassemblez vos fichiers avec {appName}",
    "login": "Se connecter",
    "password": "Mot de passe",
    "pricing": "{appName} ne coûte que {price} $ par mois après votre essai gratuit de {trial} jours !",
    "requires_action": "Vous devez compléter une action supplémentaire",
    "requires_payment_method": "Échec du paiement, veuillez choisir une autre carte",
    "stripeSecurityInformation": "Your payment information will be securely processed by Stripe. If you're unhappy for any reason, we'll refund your unused subscription balance—no questions asked."
  }
}
</i18n>
<template>
   <v-container fluid fill-height>
    <v-layout align-center justify-center>
      <v-overlay :value="cardPaymentHandled">
      </v-overlay>
      <v-flex>
        <v-flex xs12 class="text-xs-center" sm4 offset-sm4 mt-5>
          <div class="headline">{{ $t('title', { appName }) }}</div>
        </v-flex>
        <v-flex xs12 sm6 offset-sm3 mt-3>
          <select-plans
            :currencies="currencies"
            :plans="plans"
            @select-plan-update="handlePlanSelected"/>
        </v-flex>
        <v-flex xs12 sm4 offset-sm4 mt-3>
          <v-card>
            <v-card-title primary-title>
              <div class="headline">
                {{ $t('createAccountTitle') }}
              </div>
            </v-card-title>
            <v-card-text>
              <v-form @submit.prevent="signUpUser">
                <messages-list :messages="messages"/>
                <v-layout column>
                  <v-text-field
                    name="email"
                    :label="$t('email')"
                    id="email"
                    type="email"
                    v-model.trim="email"
                    @input="$v.email.$touch()"
                    :rules="emailRules"
                    :disabled="cardPaymentHandled"/>
                  <v-text-field
                    name="password"
                    :label="$t('password')"
                    id="password"
                    type="password"
                    v-model.trim="password"
                    @input="$v.password.$touch()"
                    :rules="passwordRules"
                  />
                  <v-text-field
                    name="confirmPassword"
                    :label="$t('confirmPassword')"
                    id="confirmPassword"
                    type="password"
                    v-model.trim="passwordConfirm"
                    @input="$v.passwordConfirm.$touch()"
                    :rules="passwordConfirmationRules"/>
                  <v-flex class="mt-3">
                    <card class='stripe-card'
                        :stripe='stripePublicKey'
                        @change='checkoutComplete = $event.complete'
                        :options='stripeOptions'/>
                    </v-flex>
                    <p class="mt-5">
                      {{ $t('stripeSecurityInformation') }}
                    </p>
                  <v-flex class="text-center" mt-5>
                    <v-btn color="primary" type="submit" :disabled="isSubmitDisabled" :loading="loading">{{ $t('createAccount') }}</v-btn>
                  </v-flex>
                </v-layout>
              </v-form>
              <v-flex class="mt-3">
                {{ $t('alreadyHaveAccount') }} <router-link to="/login">{{ $t('login') }}</router-link>
              </v-flex>
            </v-card-text>
          </v-card>
        </v-flex>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import { Card, createToken, handleCardPayment } from 'vue-stripe-elements-plus'
import messagesList from '@/components/messages-list'
import selectPlans from '@/components/select-plans'
import { email, maxLength, minLength, required, sameAs } from 'vuelidate/lib/validators'
import i18n from '@/plugins/vue-i18n'

export default {
  name: 'signup',
  components: {
    Card,
    messagesList,
    selectPlans
  },
  data () {
    return {
      actionCompleted: false,
      cardPaymentHandled: false,
      checkoutComplete: false,
      email: '',
      loading: false,
      messages: [],
      name: '',
      password: '',
      plan: null,
      selectedPlan: null,
      passwordConfirm: '',
      stripeOptions: { hidePostalCode: true },
      stripePublicKey: process.env.VUE_APP_STRIPE_PUBLIC_KEY,
      subscriptionId: null,
      customerId: null
    }
  },
  methods: {
    handlePlanSelected ($event) {
      this.plan = $event
    },
    async signUpUser () {
      try {
        this.messages = []
        this.loading = true
        const { token } = await createToken()
        const { clientSecret, status, subscriptionId, customerId } = await this.$store.dispatch('signUpUser', {
          actionCompleted: this.actionCompleted,
          customerId: this.customerId,
          email: this.email,
          password: this.password,
          plan: this.plan,
          subscriptionId: this.subscriptionId,
          token
        })
        this.subscriptionId = subscriptionId
        this.customerId = customerId
        this.loading = false
        if (status === 'succeeded') {
          this.$router.push('/connections')
        } else if (status === 'requires_action') {
          this.messages = [this.$t('requires_action')]
          // handle card actions
          this.cardPaymentHandled = true
          const { paymentIntent, error } = await handleCardPayment(clientSecret)
          console.log('error', error)
          this.cardPaymentHandled = false
          if (error) {
            this.messages = [error.message]
          }
          if (paymentIntent) {
            this.actionCompleted = true
            this.signUpUser()
          }
        } else if (status === 'requires_payment_method') {
          this.messages = [this.$t('requires_payment_method')]
        } else if (status === 'incorrect_cvc') {
          this.messages = [this.$t('incorrect_cvc')]
        }
      } catch (err) {
        console.log(err)
        if (err && err.response && err.response.data && err.response.data.errors) {
          this.messages = err.response.data.errors.map(error => this.$t(error.msg))
        }
        this.loading = false
      }
    }
  },
  computed: {
    appName () {
      return this.$store.getters.getAppName
    },
    currencies () {
      return this.$store.getters.getCurrencies
    },
    emailRules () {
      return [
        () => (!this.$v.email.$dirty || this.$v.email.required) || this.$t('validation.required'),
        () => this.$v.email.maxLength || this.$t('validation.maxLength', { max: 255 }),
        () => this.$v.email.email || this.$t('validation.email')]
    },
    isSubmitDisabled () {
      return !this.checkoutComplete || this.$v.$invalid || this.loading
    },
    passwordRules () {
      return [
        () => (!this.$v.password.$dirty || this.$v.password.required) || this.$t('validation.required'),
        () => (!this.$v.password.$dirty || this.$v.password.minLength) || this.$t('validation.minLength', { min: 6 })]
    },
    passwordConfirmationRules () {
      return [
        () => (!this.$v.passwordConfirm.$dirty || this.$v.passwordConfirm.required) || this.$t('validation.required'),
        () => this.$v.passwordConfirm.sameAsPassword || this.$t('validation.identicalPassword')]
    },
    plans () {
      return this.$store.getters.getPlans
    }
  },
  created () {
    this.stripeOptions.elements = {
      locale: i18n.locale
    }
    this.$store.dispatch('loadAppPlans')
  },
  validations: {
    email: {
      email,
      maxLength: maxLength(255),
      required
    },
    password: {
      minLength: minLength(6),
      required
    },
    passwordConfirm: {
      sameAsPassword: sameAs('password'),
      required
    },
    plan: {
      required
    }
  }
}
</script>

<style>

</style>
